import {useSelector} from 'react-redux';
import {useIsAccountingAvailable} from './useIsAccountingAvailable';

/**
 * Checks whether the company runs accounting in Manual ERP (export) mode rather than through the
 * NetSuite integration.
 *
 * The requirements describe a tri-state `netsuite_connected` (TRUE / FALSE / MANUAL), but
 * app-accounting exposes a plain boolean. The three states are composed from the two gates the
 * frontend already has, rather than looked for in a single field:
 *
 * - FALSE  — `accounting_module` is off and the section is hidden entirely
 * - TRUE   — accounting is available and this returns false
 * - MANUAL — accounting is available and this returns true
 *
 * `companySettings` is fetched per company by `UserCompanyInitializer`, so this is a plain selector
 * rather than a fetch. That fetch is deliberately NOT bundled with the rest of the accounting
 * reference data, which only loads once the user opens an accounting page — the mode gates
 * surfaces that exist elsewhere, so it has to be available before then.
 *
 * It defaults to false, which means a company with nothing set behaves exactly as it does today:
 * the flag can only ever add export mode, never take NetSuite away from a company that has it. The
 * same default applies while the settings are still in flight, so callers get NetSuite behaviour
 * for a beat before an export-mode company resolves.
 *
 * @returns {boolean} True when the accounting section should render in export mode.
 *
 * @example
 * const isExportMode = useIsAccountingExportMode();
 */
export const useIsAccountingExportMode = () => {
  const isAccountingAvailable = useIsAccountingAvailable();
  const isExportMode = useSelector(state => state.accounting?.companySettings?.is_export_mode === true);

  return isAccountingAvailable && isExportMode;
};
